import {
  checkBackup,
  createBackup,
  hasErrors,
  summarizeBackup,
  type Backup,
  type BackupSummary,
} from '../domain'
import type { AtrilDb } from './db'
import { StorageError, setMeta } from './repository'

/**
 * Copia de seguridad y restauración sobre la base de datos (§9.5). Safari 15 no garantiza que
 * conserve los datos, así que esta es la red de seguridad hasta que llegue la sincronización.
 */
export async function exportBackup(
  db: AtrilDb,
  now: string,
  preferences?: Record<string, unknown>,
): Promise<Backup> {
  // Una sola transacción de lectura: la copia es una instantánea coherente.
  return db.transaction('r', [db.songs, db.arrangements, db.revisions, db.practice], async () => {
    const [songs, arrangements, revisions, practice] = await Promise.all([
      db.songs.toArray(),
      db.arrangements.toArray(),
      db.revisions.toArray(),
      db.practice.toArray(),
    ])
    return createBackup(
      { songs, arrangements, revisions, practice, ...(preferences ? { preferences } : {}) },
      now,
    )
  })
}

/** Anota que se acaba de guardar una copia: apaga el recordatorio. */
export async function markBackupDone(db: AtrilDb, now: string): Promise<void> {
  await setMeta(db, 'lastBackupAt', Date.parse(now))
}

export type RestoreMode = 'replace' | 'merge'

export interface RestoreReport {
  mode: RestoreMode
  /** Lo que se ha añadido a la base de datos. */
  added: BackupSummary
  /** Lo que ya estaba (solo en «combinar») y se ha dejado como estaba. */
  skipped: BackupSummary
}

/**
 * Restaura una copia.
 * - `replace`: sustituye todo lo que hay por el contenido de la copia.
 * - `merge`: añade lo que falta y deja lo que ya existe como está (las revisiones son
 *   inmutables: la misma ID es la misma revisión). El puntero principal local no se mueve.
 *
 * Todo ocurre en una transacción: si algo falla, no cambia nada.
 */
export async function restoreBackup(
  db: AtrilDb,
  backup: Backup,
  mode: RestoreMode,
  now: string,
): Promise<RestoreReport> {
  const issues = checkBackup(backup)
  if (hasErrors(issues)) {
    throw new StorageError('invalid', 'La copia no es coherente: no se restaura nada')
  }

  // La memoria de práctica de un arreglo que no viene en la copia se descarta.
  const arrangementIds = new Set(backup.arrangements.map((arrangement) => arrangement.id))
  const practice = (backup.practice ?? []).filter((item) => arrangementIds.has(item.arrangementId))

  return db.transaction(
    'rw',
    [db.songs, db.arrangements, db.revisions, db.drafts, db.practice, db.meta],
    async () => {
      if (mode === 'replace') {
        await Promise.all([
          db.songs.clear(),
          db.arrangements.clear(),
          db.revisions.clear(),
          db.drafts.clear(),
          db.practice.clear(),
        ])
        await db.songs.bulkAdd(backup.songs)
        await db.arrangements.bulkAdd(backup.arrangements)
        await db.revisions.bulkAdd(backup.revisions)
        await db.practice.bulkAdd(practice)
        await setMeta(db, 'lastBackupAt', Date.parse(now))
        const first = await db.meta.get('firstDataAt')
        if (!first || first.value === null) await setMeta(db, 'firstDataAt', Date.parse(now))
        return {
          mode,
          added: summarizeBackup(backup),
          skipped: { songs: 0, arrangements: 0, revisions: 0 },
        }
      }

      const missing = async <T extends { id: string }>(
        table: { bulkGet(keys: string[]): Promise<(T | undefined)[]> },
        items: T[],
      ) => {
        const existing = await table.bulkGet(items.map((item) => item.id))
        return items.filter((_, index) => existing[index] === undefined)
      }
      const songs = await missing(db.songs, backup.songs)
      const arrangements = await missing(db.arrangements, backup.arrangements)
      const revisions = await missing(db.revisions, backup.revisions)
      await db.songs.bulkAdd(songs)
      await db.arrangements.bulkAdd(arrangements)
      await db.revisions.bulkAdd(revisions)
      // La práctica local manda: solo se añade la de los arreglos que no tenían.
      const existingPractice = await db.practice.bulkGet(practice.map((item) => item.arrangementId))
      await db.practice.bulkAdd(practice.filter((_, index) => !existingPractice[index]))
      const added = {
        songs: songs.length,
        arrangements: arrangements.length,
        revisions: revisions.length,
      }
      if (added.songs + added.arrangements + added.revisions > 0) {
        await setMeta(db, 'lastChangeAt', Date.parse(now))
        const first = await db.meta.get('firstDataAt')
        if (!first || first.value === null) await setMeta(db, 'firstDataAt', Date.parse(now))
      }
      return {
        mode,
        added,
        skipped: {
          songs: backup.songs.length - songs.length,
          arrangements: backup.arrangements.length - arrangements.length,
          revisions: backup.revisions.length - revisions.length,
        },
      }
    },
  )
}
